import { useState, useEffect, useCallback } from "react";
import { getRecordedAudio, hasRecordedAudio } from "../utils/audioStorage";

export { hasRecordedAudio, getRecordedAudio, saveRecordedAudio, deleteRecordedAudio, loadRecordedAudios, getTalePageAudioKey, getRecordedAudiosSet } from "../utils/audioStorage";

// ============================================================================
// SINGLETON KABYLE TTS & AUDIO ENGINE (HIGH-PERFORMANCE, ZERO-LEAK)
// ============================================================================

interface KabyleTTSState {
  isPlaying: boolean;
  currentPlayingText: string | null;
  isLoading: boolean;
  error: string | null;
}

let globalState: KabyleTTSState = {
  isPlaying: false,
  currentPlayingText: null,
  isLoading: false,
  error: null,
};

type StateListener = (state: KabyleTTSState) => void;
const listeners = new Set<StateListener>();

function notify() {
  const frozen = { ...globalState };
  listeners.forEach((l) => {
    try {
      l(frozen);
    } catch {
      // ignore
    }
  });
}

function updateState(partial: Partial<KabyleTTSState>) {
  globalState = { ...globalState, ...partial };
  notify();
}

let activeAudio: HTMLAudioElement | null = null;
let cachedVoice: SpeechSynthesisVoice | null = null;
let voicesLoaded = false;
const ttsBlobUrlCache = new Map<string, string>();

// Cache voices once
function initVoices() {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  const updateVoices = () => {
    const voices = window.speechSynthesis.getVoices();
    if (voices && voices.length > 0) {
      cachedVoice =
        voices.find((v) => v.lang.startsWith("fr")) ||
        voices.find((v) => v.lang.startsWith("es")) ||
        voices.find((v) => v.lang.startsWith("it")) ||
        voices[0] ||
        null;
      voicesLoaded = true;
    }
  };

  updateVoices();
  if (window.speechSynthesis.onvoiceschanged !== undefined) {
    window.speechSynthesis.onvoiceschanged = updateVoices;
  }
}

if (typeof window !== "undefined") {
  initVoices();
}

/**
 * Phonetic transliteration from Berber/Kabyle latin alphabet to French/European phonemes
 */
export function kabyleToPhonetic(text: string): string {
  return text
    .replace(/ɣ/g, "rh")
    .replace(/Ɣ/g, "Rh")
    .replace(/ɛ/g, "a")
    .replace(/Ɛ/g, "A")
    .replace(/č/g, "tch")
    .replace(/Č/g, "Tch")
    .replace(/c/g, "ch")
    .replace(/C/g, "Ch")
    .replace(/ǧ/g, "dj")
    .replace(/Ǧ/g, "Dj")
    .replace(/u/g, "ou")
    .replace(/U/g, "Ou")
    .replace(/ṭ/g, "t")
    .replace(/Ṭ/g, "T")
    .replace(/ḍ/g, "d")
    .replace(/Ḍ/g, "D")
    .replace(/ṣ/g, "s")
    .replace(/Ṣ/g, "S")
    .replace(/ẓ/g, "z")
    .replace(/Ẓ/g, "Z")
    .replace(/ḥ/g, "h")
    .replace(/Ḥ/g, "H")
    .replace(/x/g, "kh")
    .replace(/X/g, "Kh")
    .replace(/q/g, "k")
    .replace(/Q/g, "K")
    .replace(/ṛ/g, "r")
    .replace(/Ṛ/g, "R")
    .replace(/[.,?!;:«»"()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Stop any active audio immediately (called globally without hammering the browser)
 */
export function stopKabyleAudio() {
  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.currentTime = 0;
      activeAudio.src = "";
    } catch {
      // Ignore
    }
    activeAudio = null;
  }

  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        // Ignore
      }
    }
  }

  if (globalState.isPlaying || globalState.isLoading || globalState.currentPlayingText !== null) {
    updateState({
      isPlaying: false,
      currentPlayingText: null,
      isLoading: false,
    });
  }
}

/**
 * Play synthesized speech or recorded audio cleanly
 */
export async function playKabyleAudio(
  text: string,
  options?: { slow?: boolean; rate?: number }
): Promise<void> {
  if (!text) return;

  // Stop any previous speech/audio
  stopKabyleAudio();

  const normalizedWord = text.toLowerCase().trim();
  updateState({
    isLoading: true,
    isPlaying: true,
    currentPlayingText: text,
    error: null,
  });

  // 1. Check for Custom User-Recorded Audio
  const recordedDataUrl = getRecordedAudio(normalizedWord);
  if (recordedDataUrl) {
    try {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = recordedDataUrl;
      
      if (options?.slow) {
        audio.playbackRate = 0.75;
      } else if (options?.rate) {
        audio.playbackRate = options.rate;
      }
      activeAudio = audio;

      audio.onended = () => {
        if (activeAudio === audio) {
          activeAudio = null;
        }
        updateState({
          isPlaying: false,
          currentPlayingText: null,
          isLoading: false,
        });
      };

      audio.onerror = (e) => {
        console.warn('Audio playback error for recorded voice:', e);
        if (activeAudio === audio) {
          activeAudio = null;
        }
        updateState({
          isPlaying: false,
          currentPlayingText: null,
          isLoading: false,
        });
      };

      updateState({ isLoading: false });

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      return;
    } catch (err) {
      console.warn('Playback of recorded voice failed, falling back to synthesis:', err);
    }
  }

  // 2. Try High-Fidelity Server-Side Amazigh TTS (ayymen/Amazigh-tts MMS Model)
  try {
    let audioUrl = ttsBlobUrlCache.get(normalizedWord);

    if (!audioUrl) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 9000);

      const res = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: normalizedWord, variant: "Taqbaylit" }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("audio")) {
          const blob = await res.blob();
          if (blob && blob.size > 200) {
            audioUrl = URL.createObjectURL(blob);
            ttsBlobUrlCache.set(normalizedWord, audioUrl);
          }
        }
      }
    }

    if (audioUrl) {
      const audio = new Audio();
      audio.preload = "auto";
      audio.src = audioUrl;

      if (options?.slow) {
        audio.playbackRate = 0.75;
      } else if (options?.rate) {
        audio.playbackRate = options.rate;
      }
      activeAudio = audio;

      audio.onended = () => {
        if (activeAudio === audio) {
          activeAudio = null;
        }
        updateState({
          isPlaying: false,
          currentPlayingText: null,
          isLoading: false,
        });
      };

      audio.onerror = (e) => {
        console.warn("TTS Audio playback error:", e);
        if (activeAudio === audio) {
          activeAudio = null;
        }
        updateState({
          isPlaying: false,
          currentPlayingText: null,
          isLoading: false,
        });
      };

      updateState({ isLoading: false });

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      return;
    }
  } catch (serverTtsErr) {
    console.warn("Server Amazigh TTS request failed, switching to client speech synthesis fallback:", serverTtsErr);
  }

  // 3. Fallback to Browser SpeechSynthesis with Phonetic Transliteration
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    updateState({
      isPlaying: false,
      currentPlayingText: null,
      isLoading: false,
    });
    return;
  }

  try {
    if (!voicesLoaded) {
      initVoices();
    }

    const phonetic = kabyleToPhonetic(text);
    const utterance = new SpeechSynthesisUtterance(phonetic);
    if (cachedVoice) {
      utterance.voice = cachedVoice;
    }
    utterance.rate = options?.slow ? 0.68 : options?.rate || 0.88;
    utterance.pitch = 1.0;

    utterance.onstart = () => {
      updateState({ isLoading: false, isPlaying: true, currentPlayingText: text });
    };

    utterance.onend = () => {
      updateState({
        isPlaying: false,
        currentPlayingText: null,
        isLoading: false,
      });
    };

    utterance.onerror = () => {
      updateState({
        isPlaying: false,
        currentPlayingText: null,
        isLoading: false,
      });
    };

    updateState({ isLoading: false });
    window.speechSynthesis.speak(utterance);
  } catch {
    updateState({
      isPlaying: false,
      currentPlayingText: null,
      isLoading: false,
    });
  }
}

/**
 * Ultra-high-performance Hook for individual word audio buttons.
 * Only triggers re-renders for the specific word currently starting or stopping playback,
 * preventing hundreds of simultaneous button re-renders across the dictionary & challenges.
 */
export function useWordTTS(wordText: string) {
  const norm = wordText ? wordText.toLowerCase().trim() : '';

  const [isPlayingThis, setIsPlayingThis] = useState<boolean>(() => {
    return Boolean(
      globalState.isPlaying &&
      globalState.currentPlayingText?.toLowerCase().trim() === norm
    );
  });

  const [isLoadingThis, setIsLoadingThis] = useState<boolean>(() => {
    return Boolean(
      globalState.isLoading &&
      globalState.currentPlayingText?.toLowerCase().trim() === norm
    );
  });

  useEffect(() => {
    if (!norm) {
      setIsPlayingThis(false);
      setIsLoadingThis(false);
      return;
    }

    const initialTarget = globalState.currentPlayingText?.toLowerCase().trim() === norm;
    setIsPlayingThis(Boolean(globalState.isPlaying && initialTarget));
    setIsLoadingThis(Boolean(globalState.isLoading && initialTarget));

    const listener: StateListener = (nextState) => {
      const isTarget = nextState.currentPlayingText?.toLowerCase().trim() === norm;
      const nextPlaying = Boolean(nextState.isPlaying && isTarget);
      const nextLoading = Boolean(nextState.isLoading && isTarget);

      setIsPlayingThis((prev) => (prev !== nextPlaying ? nextPlaying : prev));
      setIsLoadingThis((prev) => (prev !== nextLoading ? nextLoading : prev));
    };

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [norm]);

  const play = useCallback(
    (options?: { slow?: boolean; rate?: number }) => {
      return playKabyleAudio(wordText, options);
    },
    [wordText]
  );

  return {
    isPlaying: isPlayingThis,
    isLoading: isLoadingThis,
    play,
  };
}

/**
 * Lightweight React Hook that subscribes to the shared Singleton Audio State
 * Does NOT instantiate audio contexts or heavy unmount cancel loops.
 */
export function useKabyleTTS() {
  const [state, setState] = useState<KabyleTTSState>(globalState);

  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const playTTS = useCallback((text: string, options?: { slow?: boolean; rate?: number }) => {
    return playKabyleAudio(text, options);
  }, []);

  const stopAudio = useCallback(() => {
    stopKabyleAudio();
  }, []);

  return {
    playTTS,
    stopAudio,
    isPlaying: state.isPlaying,
    currentPlayingText: state.currentPlayingText,
    loading: state.isLoading,
    isLoading: state.isLoading,
    error: state.error,
  };
}
